import { format, parseISO } from 'date-fns';

export function formatWholeDays(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'Not enough data';
  }

  return `${Math.round(value)} days`;
}

/**
 * Titoli catalogo spesso in ALL CAPS: per la UI usiamo title case leggibile.
 * Se non è “urlato”, resta invariato.
 */
export function formatEntityTitleForDisplay(raw: string): string {
  if (!raw || raw === 'Not enough data') return raw;
  const letters = raw.replace(/[^A-Za-z]/g, '');
  if (letters.length < 4 || raw !== raw.toUpperCase()) return raw;
  return raw.toLowerCase().replace(/\b[a-z]/g, (m) => m.toUpperCase());
}

export function formatShortDays(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'Not enough data';
  }

  return `${Math.round(value)}d`;
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return `${Math.round(value * 100)}%`;
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'Not enough data';
  }

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatCompactCurrency(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatRatio(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'Not enough data';
  }

  return `${value.toFixed(2)}x`;
}

/** Etichetta compatta per il periodo di confronto (es. "Mar 1–Mar 31, 2026"). */
export function formatShortCompareRange(from: string, to: string): string {
  if (!from || !to) return '';
  try {
    const a = parseISO(from.length <= 10 ? `${from}T12:00:00` : from);
    const b = parseISO(to.length <= 10 ? `${to}T12:00:00` : to);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
      return `${from}–${to}`;
    }
    if (a.getFullYear() === b.getFullYear()) {
      return `${format(a, 'MMM d')}–${format(b, 'MMM d, yyyy')}`;
    }
    return `${format(a, 'MMM d, yyyy')}–${format(b, 'MMM d, yyyy')}`;
  } catch {
    return `${from}–${to}`;
  }
}

export type CompareDeltaVisual = {
  /** Es. "8%" o "12pp" per repeat rate */
  pctLabel: string;
  direction: 'up' | 'down' | 'neutral';
  good: boolean;
  /** Es. "vs Mar 1–Mar 31, 2026" */
  vsLabel: string;
};

function vsPrefix(compareRangeFrom: string, compareRangeTo: string): string {
  const r = formatShortCompareRange(compareRangeFrom, compareRangeTo);
  return r ? `vs ${r}` : '';
}

/**
 * Variazione % rispetto al periodo di confronto (baseline = compare).
 * `lowerIsBetter`: es. giorni — meno giorni nel primario = esito positivo (freccia giù verde).
 */
export function buildCompareDeltaVersusBaseline(
  primary: number | null | undefined,
  compare: number | null | undefined,
  options: {
    lowerIsBetter: boolean;
    compareRangeFrom: string;
    compareRangeTo: string;
  },
): CompareDeltaVisual | null {
  const { lowerIsBetter, compareRangeFrom, compareRangeTo } = options;
  const vsLabel = vsPrefix(compareRangeFrom, compareRangeTo);
  if (
    primary == null ||
    compare == null ||
    Number.isNaN(primary) ||
    Number.isNaN(compare)
  ) {
    return null;
  }
  if (primary === compare) {
    return {
      pctLabel: '0%',
      direction: 'neutral',
      good: true,
      vsLabel,
    };
  }
  if (compare === 0) {
    return null;
  }

  const pctRaw = Math.round(
    (Math.abs(compare - primary) / Math.abs(compare)) * 100,
  );
  const pctLabel = `${pctRaw}%`;

  if (lowerIsBetter) {
    if (primary < compare) {
      return { pctLabel, direction: 'down', good: true, vsLabel };
    }
    return { pctLabel, direction: 'up', good: false, vsLabel };
  }
  if (primary > compare) {
    return { pctLabel, direction: 'up', good: true, vsLabel };
  }
  return { pctLabel, direction: 'down', good: false, vsLabel };
}

/** Delta repeat rate in punti percentuali (0–1 → pp). Più alto nel primario = meglio. */
export function buildCompareDeltaRepeatRatePp(
  primary: number | null | undefined,
  compare: number | null | undefined,
  compareRangeFrom: string,
  compareRangeTo: string,
): CompareDeltaVisual | null {
  const vsLabel = vsPrefix(compareRangeFrom, compareRangeTo);
  if (
    primary == null ||
    compare == null ||
    Number.isNaN(primary) ||
    Number.isNaN(compare)
  ) {
    return null;
  }
  const pp = Math.round((primary - compare) * 100);
  if (pp === 0) {
    return {
      pctLabel: '0pp',
      direction: 'neutral',
      good: true,
      vsLabel,
    };
  }
  const pctLabel = `${Math.abs(pp)}pp`;
  if (pp > 0) {
    return { pctLabel, direction: 'up', good: true, vsLabel };
  }
  return { pctLabel, direction: 'down', good: false, vsLabel };
}
